import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const StatusIndicator = ({ 
  status = 'idle', 
  count = 0, 
  label = 'Processing',
  showPulse = true,
  size = 'default',
  className = ''
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (status === 'processing' && showPulse) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status, showPulse]);

  const getStatusConfig = () => {
    switch (status) {
      case 'processing':
        return {
          icon: 'Loader2',
          color: 'text-accent',
          bgColor: 'bg-accent/10',
          borderColor: 'border-accent/20',
          pulseColor: 'bg-accent'
        };
      case 'success':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          pulseColor: 'bg-success'
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          color: 'text-error',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          pulseColor: 'bg-error'
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          pulseColor: 'bg-warning'
        };
      default:
        return {
          icon: 'Circle',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          pulseColor: 'bg-muted'
        };
    }
  };

  const config = getStatusConfig();
  const sizeClasses = {
    sm: 'h-6 px-2 text-xs',
    default: 'h-8 px-3 text-sm',
    lg: 'h-10 px-4 text-base'
  };

  const iconSizes = {
    sm: 12,
    default: 16,
    lg: 18
  };

  return (
    <div className={`
      relative inline-flex items-center space-x-2 rounded-full border
      ${config?.bgColor} ${config?.borderColor} ${sizeClasses?.[size]}
      transition-all duration-150 ease-out
      ${className}
    `}>
      {/* Pulse Animation */}
      {showPulse && status === 'processing' && (
        <div className={`
          absolute inset-0 rounded-full ${config?.pulseColor} opacity-20
          ${isAnimating ? 'animate-ping' : ''}
        `} />
      )}
      {/* Status Icon */}
      <div className={`relative ${config?.color}`}>
        <Icon 
          name={config?.icon} 
          size={iconSizes?.[size]} 
          color="currentColor"
          strokeWidth={2}
          className={status === 'processing' ? 'animate-spin' : ''}
        />
      </div>
      {/* Label and Count */}
      <div className="flex items-center space-x-1">
        <span className={`font-medium ${config?.color}`}>
          {label}
        </span>
        {count > 0 && (
          <span className={`
            inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5
            text-xs font-semibold rounded-full
            ${config?.color} ${config?.bgColor} border ${config?.borderColor}
          `}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </div>
    </div>
  );
};

export default StatusIndicator;

